import { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { convertToUUID, convertTo62 } from 'uuid-encoding';
import { GroupType } from 'graphql-query-contracts';
import { getProductQuery } from '../helpers/getProductQuery';
import { NewProductDetails } from 'application-types';

export function useLoadProduct(
  productId: string,
  channelId: string | undefined
) {
  const [productDetails, setProductDetails] = useState<NewProductDetails | null>(null);
  const [loadProduct, { data }] = useLazyQuery(getProductQuery, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (channelId) {
      loadProduct({
        variables: {
          getProductRequest: {
            productId,
            group: {
              id: convertToUUID(channelId),
              type: GroupType.GroupTypeActivateChannel,
            },
          },
        },
      });
    }
  }, [productId, channelId, loadProduct]);

  useEffect(() => {
    if (data) {
      const product = data?.getProduct.product;
      const details: NewProductDetails = {
        productCategoryId: product.productCategory.productCategoryId,
        productCategoryName: product.productCategory.name,
        chargeCodeId: product.chargeCode?.id,
        markupChargeCodeId: product.markupChargeCode?.id
          ? convertTo62(product.markupChargeCode.id)
          : undefined,
        name: product.name,
        description: product.description,
        rate: product.amount.toString(),
        productType: product.productCategory.productType,
        creditAmount: product.creditAmount,
        netPrice: product.sellingPrice?.netPrice?.toString(),
      };

      if (product.markup?.value > 0) {
        details.markup = {
          value: product.markup.value,
          type: product.markup.markupAmountType,
        };
      }

      if (product.tax?.value > 0) {
        details.tax = {
          value: product.tax.value,
          type: product.tax.taxAmountType,
        };
      }

      if (product.adminFee?.value > 0) {
        details.adminFee = {
          value: product.adminFee.value.toString(),
          type: product.adminFee.adminFeeAmountType,
        };
      }

      if (product.adminFeeChargeCode?.id) {
        details.adminFeeChargeCodeId = convertTo62(product.adminFeeChargeCode.id);
      }

      if (product.adminFeeTax?.rate > 0) {
        details.adminFeeTax = {
          value: product.adminFeeTax.rate.toString(),
          type: product.adminFeeTax.taxAmountType,
        };
      }

      setProductDetails(details);
    }
  }, [data]);

  return { productDetails, product: data?.getProduct.product };
}

